import * as api from "./client.js";

export type CrmWebhookLogStatus = "created" | "duplicate" | "failed" | "dropped";

export type CrmWebhookLog = {
  id: string;
  correlationId: string | null;
  crmFirmId: string | null;
  regionName: string | null;
  rawPayload: Record<string, unknown>;
  responseStatusCode: number;
  responseBody: Record<string, unknown> | null;
  status: CrmWebhookLogStatus;
  subscriptionId: string | null;
  errorMessage: string | null;
  errorCode: string | null;
  handled: boolean;
  autoResolvedData: Record<string, unknown> | null;
  createdAt: string;
};

export type CrmWebhookLogsListResponse = {
  logs: CrmWebhookLog[];
  total: number;
};

export type CrmWebhookLogSingleResponse = {
  log: CrmWebhookLog;
};

export const crmWebhookLogs = {
  getAll: async (filters?: {
    search?: string;
    status?: CrmWebhookLogStatus;
    errorCode?: string;
    correlationId?: string;
    handled?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CrmWebhookLogsListResponse> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("search", filters.search);
    if (filters?.status) params.append("status", filters.status);
    if (filters?.errorCode) params.append("errorCode", filters.errorCode);
    if (filters?.correlationId) params.append("correlationId", filters.correlationId);
    if (filters?.handled !== undefined) params.append("handled", String(filters.handled));
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<CrmWebhookLogsListResponse>(`/v1/fulfilment/crm-webhook-logs${query}`);
  },

  getById: async (id: string): Promise<CrmWebhookLog> => {
    const response = await api.get<CrmWebhookLogSingleResponse>(
      `/v1/fulfilment/crm-webhook-logs/${id}`,
    );
    return response.log;
  },

  getByCorrelationId: async (correlationId: string): Promise<CrmWebhookLog> => {
    const response = await api.get<CrmWebhookLogSingleResponse>(
      `/v1/fulfilment/crm-webhook-logs/by-correlation-id/${encodeURIComponent(correlationId)}`,
    );
    return response.log;
  },

  getUnhandledCount: async (): Promise<{ count: number }> => {
    return api.get<{ count: number }>("/v1/fulfilment/crm-webhook-logs/unhandled-count");
  },

  retry: async (id: string): Promise<{ status: string; logId: string }> => {
    return api.post<{ status: string; logId: string }>(
      `/v1/fulfilment/crm-webhook-logs/${id}/retry`,
    );
  },

  retryBulk: async (
    ids: string[],
  ): Promise<{
    results: Array<{ id: string; status: string; logId?: string; error?: string }>;
  }> => {
    return api.post<{
      results: Array<{ id: string; status: string; logId?: string; error?: string }>;
    }>("/v1/fulfilment/crm-webhook-logs/retry", { ids });
  },

  retryAll: async (filters?: {
    status?: string;
    errorCode?: string;
    handled?: boolean;
    search?: string;
  }): Promise<{
    total: number;
    created: number;
    failed: number;
    dropped: number;
    duplicate: number;
    unchanged: number;
    errors: number;
  }> => {
    return api.post("/v1/fulfilment/crm-webhook-logs/retry-all", filters);
  },

  markHandledBulk: async (ids: string[]): Promise<{ updated: number }> => {
    return api.patch<{ updated: number }>("/v1/fulfilment/crm-webhook-logs/mark-handled", {
      ids,
    });
  },
};
